// Columna de detalle de Inventario: aviso sin SKU elegido o la ficha del SKU con cabecera, resumen y pestañas.
import { useState } from 'react';
import { Boxes } from 'lucide-react';
import type { InventoryLensPeriod } from '@/api/services/retail-catalog/backoffice';
import { cn } from '@/lib/cn';
import { PromotionsTab } from '@/shared/patterns/Promotions';
import { useInventorySku, useSkuPrices } from '../api/useInventorySku';
import { useOpenTransfers } from '../api/useInventoryMovements';
import { useSkuSaleRows } from '../api/useSkuSaleRows';
import { useCatalogProduct } from '../api/useCatalogWrites';
import {
    activeTabFor,
    currentPriceOf,
    headerName,
    openTransfersBadge,
    storeInfo,
    visibleTabs,
    type DetailTab,
    type StoreDirectory,
} from '../lib/inventoryDetail';
import type { InventoryRow } from '../lib/inventoryList';
import { openTransfersOf } from '../lib/inventoryMovements';
import { skuPromotionsState } from '../lib/inventoryPromotions';
import { useInventoryAbilities } from '../lib/inventoryPermissions';
import { CardMovementsTab } from './detail/CardMovementsTab';
import { CardStockTab } from './detail/CardStockTab';
import { PriceHistoryModal } from './detail/PriceHistoryModal';
import { PriceTab } from './detail/PriceTab';
import { ProductTab } from './detail/ProductTab';
import { SalesTab } from './detail/SalesTab';
import { StockMovementsTab } from './detail/StockMovementsTab';
import { SkuHeader } from './detail/SkuHeader';

export interface InventoryDetailContext {
    period: InventoryLensPeriod;
    /** Tienda del filtro; ausente en consolidado. */
    storeId: string | undefined;
    stores: StoreDirectory;
    /** Nombre de la zona por tienda, del padrón de retail-org. */
    zones: ReadonlyMap<string, string>;
    retailerId: string | null;
    today: string;
}

function EmptyDetail() {
    return (
        <div className="flex flex-1 flex-col items-center justify-center px-5 py-10 text-center text-muted-foreground">
            <Boxes className="mb-3 size-10 text-muted-foreground/40" aria-hidden />
            <div className="mb-1 text-[13px] font-semibold text-foreground">Selecciona un SKU</div>
            <div className="text-[11.5px]">
                Elige una fila de la izquierda para ver stock por tienda, movimiento y registrar
                movimientos.
            </div>
        </div>
    );
}

/** Promos de venta de la ficha; el servicio no lleva libro y solo la pide al abrir la pestaña. */
function useSkuPromotions(row: InventoryRow, ctx: InventoryDetailContext, open: boolean) {
    const hasSheet = row.kind !== 'CARD' && row.kind !== 'SERVICE';
    const wanted = row.kind !== 'CARD' && (hasSheet || open);
    const query = useInventorySku(wanted ? row.sku : null, ctx.period, ctx.storeId);
    return skuPromotionsState(query.data?.pages.at(0), query.isPending, query.error);
}

/** Traspasos abiertos del SKU para el conteo de la pestaña de stock; sin permiso de lectura, sin conteo. */
function useSkuOpenTransferCount(sku: string, hasTab: boolean): number | undefined {
    const { canReadTransfers } = useInventoryAbilities();
    const query = useOpenTransfers(canReadTransfers && hasTab);
    if (!query.data) return undefined;
    return openTransfersOf(query.data.items, sku).length;
}

function TabButton({
    active,
    label,
    count,
    countLabel,
    onClick,
}: {
    active: boolean;
    label: string;
    count?: number | string;
    /** Qué cuenta el número, para `title` y lector de pantalla. */
    countLabel?: string;
    onClick: () => void;
}) {
    return (
        <button
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={countLabel === undefined ? undefined : `${label}, ${countLabel}`}
            onClick={onClick}
            className={cn(
                '-mb-px inline-flex items-center gap-1 whitespace-nowrap border-b-2 px-2 py-1.5 text-[11px] transition-colors',
                active
                    ? 'border-primary font-semibold text-foreground'
                    : 'border-transparent font-medium text-muted-foreground hover:text-foreground',
            )}
        >
            {label}
            {count === undefined ? null : (
                <span
                    title={countLabel}
                    className={cn(
                        'inline-flex items-center rounded-[3px] px-[5px] text-[9.5px] font-bold tabular-nums',
                        active ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground',
                    )}
                >
                    {count}
                </span>
            )}
        </button>
    );
}

function SkuDetail({
    row,
    ctx,
    tab,
    onTabChange,
}: {
    row: InventoryRow;
    ctx: InventoryDetailContext;
    tab: DetailTab;
    onTabChange: (tab: DetailTab) => void;
}) {
    const [pricesOpen, setPricesOpen] = useState(false);
    const isCard = row.kind === 'CARD';
    // Una lectura del historial al elegir el SKU: da el precio vigente y su origen.
    const prices = useSkuPrices(isCard ? null : row.sku, true);
    // La misma lectura de la pestaña Producto: da la descripción para nombrar kits y servicios.
    const product = useCatalogProduct(ctx.retailerId, row.sku, !isCard);
    const name = headerName(row, product.data?.description, product.isLoading);
    const price = currentPriceOf(prices.data, row.priceCents);
    const tabs = visibleTabs(row.kind);
    const activeTab = activeTabFor(row.kind, tab);
    const promotions = useSkuPromotions(row, ctx, activeTab === 'promotions');
    // Mismas ventas que «Stock y movimientos»: el conteo sale de la caché compartida.
    const sales = useSkuSaleRows(row, ctx, activeTab === 'stock' || activeTab === 'sales');
    const salesCount = sales.available && sales.query.data ? sales.rows.length : '—';
    const promotionsCount = promotions.kind === 'ready' ? promotions.promotions.length : undefined;
    const storeCode = ctx.storeId ? storeInfo(ctx.stores, ctx.storeId).code : undefined;
    const openTransfers = openTransfersBadge(
        useSkuOpenTransferCount(row.sku, row.kind !== 'SERVICE' && row.kind !== 'KIT' && !isCard),
    );
    const tabButton = (
        key: DetailTab,
        label: string,
        count?: number | string,
        countLabel?: string,
    ) =>
        tabs.includes(key) ? (
            <TabButton
                active={activeTab === key}
                label={label}
                count={count}
                countLabel={countLabel}
                onClick={() => {
                    onTabChange(key);
                }}
            />
        ) : null;
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <SkuHeader
                row={row}
                name={name}
                period={ctx.period}
                storeCode={storeCode}
                price={price}
                onPriceClick={
                    price.cents === null || isCard
                        ? null
                        : () => {
                              setPricesOpen(true);
                          }
                }
            />
            <div className="min-h-0 flex-1 overflow-y-auto bg-muted/15">
                <div className="px-3 py-2.5">
                    <div role="tablist" className="mb-2 flex border-b border-border bg-card">
                        {tabButton('product', 'Producto')}
                        {tabButton('price', 'Precio')}
                        {tabButton(
                            'stock',
                            'Stock y movimientos',
                            openTransfers?.count,
                            openTransfers?.label,
                        )}
                        {tabButton(
                            'sales',
                            'Ventas',
                            salesCount,
                            typeof salesCount === 'number'
                                ? `${String(salesCount)} ticket${salesCount === 1 ? '' : 's'}`
                                : undefined,
                        )}
                        {tabButton(
                            'promotions',
                            'Promos',
                            promotionsCount,
                            promotionsCount === undefined
                                ? undefined
                                : `${String(promotionsCount)} vigentes`,
                        )}
                    </div>
                    {activeTab === 'product' ? (
                        <ProductTab sku={row.sku} retailerId={ctx.retailerId} />
                    ) : null}
                    {activeTab === 'price' ? <PriceTab sku={row.sku} kind={row.kind} /> : null}
                    {activeTab === 'stock' && isCard ? (
                        <>
                            <CardStockTab row={row} ctx={ctx} />
                            <CardMovementsTab ctx={ctx} />
                        </>
                    ) : null}
                    {activeTab === 'stock' && !isCard ? (
                        <StockMovementsTab key={row.sku} row={row} ctx={ctx} />
                    ) : null}
                    {activeTab === 'sales' ? <SalesTab key={row.sku} row={row} ctx={ctx} /> : null}
                    {activeTab === 'promotions' ? <PromotionsTab state={promotions} /> : null}
                </div>
            </div>
            {isCard ? null : (
                <PriceHistoryModal
                    sku={row.sku}
                    title={name ?? row.sku}
                    isOpen={pricesOpen}
                    onClose={() => {
                        setPricesOpen(false);
                    }}
                />
            )}
        </div>
    );
}

export function InventoryDetail({
    row,
    ctx,
}: {
    row: InventoryRow | null;
    ctx: InventoryDetailContext;
}) {
    // Como el mock, la pestaña elegida se conserva al cambiar de SKU.
    const [tab, setTab] = useState<DetailTab>('stock');
    if (row === null) return <EmptyDetail />;
    return <SkuDetail key={row.sku} row={row} ctx={ctx} tab={tab} onTabChange={setTab} />;
}
